import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { emailReceipt, textReceipt } from '@/api/receipts';
import { HttpError } from '@/api/client';
import { useTranslation } from '@/i18n';

type Channel = 'email' | 'sms';

interface ReceiptDeliveryProps {
    /** The stored receipt's short code. The caller shows nothing at all when there isn't one. */
    shortCode: string;
}

/**
 * A copy of the receipt, by email or text.
 *
 * The guest is already looking at the receipt, so this is not how they read it — it is how they keep it. That
 * shapes the whole thing: nothing here is a step they have to complete, so it starts collapsed, and choosing
 * a channel is what opens a field rather than the screen opening with two of them.
 *
 * <b>The confirmation stays put.</b> A guest who has just typed an address needs to see where it went and be
 * able to check it against what they meant to type, which a toast takes away after four seconds. It also
 * names the destination for the same reason the loyalty code screen does — that is how somebody spots their
 * own typo.
 *
 * Both routes are rate limited server-side, so a 429 is an expected answer rather than a fault, and it gets
 * its own sentence: "too many" tells a guest to wait, where a generic failure tells them to retry, which is
 * exactly the wrong advice.
 */
export function ReceiptDelivery({ shortCode }: ReceiptDeliveryProps) {
    const { t } = useTranslation();
    const fieldId = useId();

    const [channel, setChannel] = useState<Channel | null>(null);
    const [contact, setContact] = useState('');

    /** What was sent, and where. Kept after success so the confirmation can name it. */
    const [sent, setSent] = useState<{ channel: Channel; contact: string } | null>(null);

    const send = useMutation({
        mutationFn: async () => {
            const trimmed = contact.trim();

            if (channel === 'email') await emailReceipt(shortCode, trimmed);
            else await textReceipt(shortCode, trimmed);

            return { channel: channel!, contact: trimmed };
        },
        onSuccess: (result) => {
            setSent(result);
            setChannel(null);
            setContact('');
        },
    });

    const choose = (next: Channel) => {
        // Switching channel with a half-typed address left behind would submit an email to the SMS route.
        setChannel((current) => (current === next ? null : next));
        setContact('');
        send.reset();
    };

    const rateLimited = send.error instanceof HttpError && send.error.status === 429;

    return (
        <section className="flex flex-col gap-3" aria-labelledby={`${fieldId}-heading`}>
            <h2 id={`${fieldId}-heading`} className="font-medium">
                {t('receipt.deliveryTitle')}
            </h2>

            {/*
              * The confirmation, above the controls rather than below them: it is the answer to what the
              * guest just did, and a guest who sends a second copy to a different address should see both
              * the last outcome and the controls without scrolling between them.
              */}
            {sent && (
                <p role="status" className="text-[0.9375rem] font-medium text-pretty">
                    {t(sent.channel === 'email' ? 'receipt.emailSent' : 'receipt.smsSent', {
                        destination: sent.contact,
                    })}
                </p>
            )}

            <div className="flex gap-2.5">
                <Button
                    type="button"
                    variant={channel === 'email' ? 'default' : 'outline'}
                    size="lg"
                    className="h-12 flex-1 rounded-xl text-[0.9375rem] font-semibold"
                    aria-expanded={channel === 'email'}
                    onClick={() => choose('email')}
                >
                    {t('receipt.emailIt')}
                </Button>

                <Button
                    type="button"
                    variant={channel === 'sms' ? 'default' : 'outline'}
                    size="lg"
                    className="h-12 flex-1 rounded-xl text-[0.9375rem] font-semibold"
                    aria-expanded={channel === 'sms'}
                    onClick={() => choose('sms')}
                >
                    {t('receipt.textIt')}
                </Button>
            </div>

            {channel && (
                <form
                    className="flex flex-col gap-2.5"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (contact.trim() !== '' && !send.isPending) send.mutate();
                    }}
                >
                    <label htmlFor={fieldId} className="sr-only">
                        {t(channel === 'email' ? 'receipt.emailLabel' : 'receipt.smsLabel')}
                    </label>

                    <input
                        id={fieldId}
                        value={contact}
                        onChange={(event) => setContact(event.target.value)}
                        /*
                         * `type="text"` even for the email case. A `type="email"` field blocks submission with
                         * a browser bubble the guest cannot restyle or translate, and this app says its own
                         * errors in its own words in both languages. The server validates either way.
                         */
                        type="text"
                        inputMode={channel === 'email' ? 'email' : 'tel'}
                        autoComplete={channel === 'email' ? 'email' : 'tel'}
                        autoFocus
                        placeholder={t(
                            channel === 'email' ? 'receipt.emailPlaceholder' : 'receipt.smsPlaceholder',
                        )}
                        className="min-h-12 rounded-xl border border-border bg-background px-4 text-lg"
                    />

                    {/*
                      * Beside the button that caused it, not at the end of the content flow. The loyalty
                      * redeem error was correct in every way except position, and nobody scrolled to it.
                      */}
                    {send.isError && (
                        <p role="alert" className="text-[0.9375rem] font-medium text-destructive text-pretty">
                            {t(rateLimited ? 'receipt.deliveryTooMany' : 'receipt.deliveryFailed')}
                        </p>
                    )}

                    <Button
                        type="submit"
                        size="lg"
                        className="h-12 w-full rounded-xl text-[0.9375rem] font-semibold"
                        disabled={contact.trim() === '' || send.isPending}
                    >
                        {send.isPending ? t('receipt.sending') : t('receipt.send')}
                    </Button>
                </form>
            )}
        </section>
    );
}
